import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getPublicProfile } from '../services/userApi';
import { formatTime, formatTimeHuman } from '../utils/formatters';
import ProfileHeader from '../components/profile/ProfileHeader';
import BadgeDisplay from '../components/profile/BadgeDisplay';
import LeagueBadge from '../components/league/LeagueBadge';

function Profile() {
  const { username } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [friendshipStatus, setFriendshipStatus] = useState(null);
  const [friendActionLoading, setFriendActionLoading] = useState(false);

  const isOwnProfile = user?.username?.toLowerCase() === username?.toLowerCase();

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getPublicProfile(username);
        if (!data) {
          setError('not-found');
        } else {
          setProfileData(data);

          // Load friendship status if logged in and not own profile
          if (user && !isOwnProfile) {
            try {
              const { getFriendshipStatus } = await import('../services/friendsApi');
              const status = await getFriendshipStatus(data.id);
              setFriendshipStatus(status);
            } catch {
              // Friends API may not be available yet
            }
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [username, user, isOwnProfile]);

  const handleSendFriendRequest = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setFriendActionLoading(true);
    try {
      const { sendFriendRequest } = await import('../services/friendsApi');
      await sendFriendRequest(profileData.id);
      setFriendshipStatus({ status: 'pending', direction: 'sent' });
    } catch (err) {
      setError(err.message);
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleAcceptRequest = async () => {
    setFriendActionLoading(true);
    try {
      const { acceptFriendRequest } = await import('../services/friendsApi');
      await acceptFriendRequest(friendshipStatus.friendshipId);
      setFriendshipStatus({ status: 'accepted' });
      setProfileData((prev) => ({
        ...prev,
        friendsCount: (prev.friendsCount || 0) + 1,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    setFriendActionLoading(true);
    try {
      const { rejectFriendRequest } = await import('../services/friendsApi');
      await rejectFriendRequest(friendshipStatus.friendshipId);
      setFriendshipStatus(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleUnfriend = async () => {
    setFriendActionLoading(true);
    try {
      const { removeFriend } = await import('../services/friendsApi');
      await removeFriend(friendshipStatus.friendshipId);
      setFriendshipStatus(null);
      setProfileData((prev) => ({
        ...prev,
        friendsCount: Math.max(0, (prev.friendsCount || 0) - 1),
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setFriendActionLoading(false);
    }
  };

  // Render friend button based on status
  const renderFriendButton = () => {
    if (isOwnProfile || !user) return null;

    if (friendActionLoading) {
      return (
        <button
          type="button"
          disabled
          className="px-4 py-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg text-sm font-semibold text-puzzle-text-secondary cursor-not-allowed"
        >
          ...
        </button>
      );
    }

    if (!friendshipStatus || friendshipStatus.status === 'none') {
      return (
        <button
          type="button"
          onClick={handleSendFriendRequest}
          className="px-4 py-1.5 bg-puzzle-blue text-white rounded-lg text-sm font-semibold hover:bg-blue-600 transition-colors"
        >
          Add Friend
        </button>
      );
    }

    if (friendshipStatus.status === 'pending' && friendshipStatus.direction === 'sent') {
      return (
        <button
          type="button"
          disabled
          className="px-4 py-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg text-sm font-semibold text-puzzle-text-secondary cursor-not-allowed"
        >
          Request Sent
        </button>
      );
    }

    if (friendshipStatus.status === 'pending' && friendshipStatus.direction === 'received') {
      return (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleAcceptRequest}
            className="px-4 py-1.5 bg-puzzle-blue text-white rounded-lg text-sm font-semibold hover:bg-blue-600 transition-colors"
          >
            Accept
          </button>
          <button
            type="button"
            onClick={handleRejectRequest}
            className="px-4 py-1.5 bg-puzzle-background border border-puzzle-border rounded-lg text-sm font-semibold text-puzzle-text hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Decline
          </button>
        </div>
      );
    }

    if (friendshipStatus.status === 'accepted') {
      return (
        <button
          type="button"
          onClick={handleUnfriend}
          className="px-4 py-1.5 bg-puzzle-background border border-puzzle-border rounded-lg text-sm font-semibold text-puzzle-text hover:bg-red-50 hover:text-red-600 hover:border-red-200 dark:hover:bg-red-900/20 transition-colors"
        >
          Unfriend
        </button>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  if (error === 'not-found') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-puzzle-text mb-2">
            Profile not found
          </h1>
          <p className="text-puzzle-text-secondary">
            The user @
            {username}
            {' '}
            doesn&apos;t exist.
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-4 text-puzzle-blue hover:underline"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-puzzle-danger mb-2">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-puzzle-blue hover:underline"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const {
    stats, streak, league, badges,
  } = profileData;

  return (
    <div className="min-h-screen bg-puzzle-background py-4">
      <div className="max-w-2xl mx-auto px-4">
        {/* Profile Header */}
        <ProfileHeader
          profile={profileData}
          isOwnProfile={isOwnProfile}
          friendButton={renderFriendButton()}
        />

        {/* Friends Count */}
        {profileData.friendsCount > 0 && (
          <div className="text-center mb-6">
            <Link
              to={isOwnProfile ? '/friends' : '#'}
              className={`text-sm text-puzzle-text-secondary ${isOwnProfile ? 'hover:underline' : ''}`}
            >
              <span className="font-semibold text-puzzle-text">{profileData.friendsCount}</span>
              {' '}
              {profileData.friendsCount === 1 ? 'friend' : 'friends'}
            </Link>
          </div>
        )}

        {/* Lifetime Stats */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">Lifetime Stats</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="text-3xl mb-1">🧩</div>
              <div className="text-2xl font-bold text-puzzle-text">
                {stats.totalCompletions || 0}
              </div>
              <div className="text-sm text-puzzle-text-secondary">Puzzles Completed</div>
            </div>
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="text-3xl mb-1">⏱️</div>
              <div className="text-2xl font-bold text-puzzle-text">
                {formatTimeHuman(stats.totalPlayTime || 0)}
              </div>
              <div className="text-sm text-puzzle-text-secondary">Total Play Time</div>
            </div>
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="text-3xl mb-1">🎯</div>
              <div className="text-2xl font-bold text-puzzle-text">
                {(stats.totalMoves || 0).toLocaleString()}
              </div>
              <div className="text-sm text-puzzle-text-secondary">Total Moves</div>
            </div>
          </div>
        </section>

        {/* By Puzzle Type */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">By Puzzle Type</h2>
          <div className="space-y-4">
            <div className="bg-puzzle-background rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📅</span>
                  <div className="font-medium text-puzzle-text">Daily Puzzles</div>
                </div>
                <div className="text-2xl font-bold text-puzzle-blue">
                  {stats.completions?.daily?.count || 0}
                </div>
              </div>
            </div>
            <div className="bg-puzzle-background rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🔒</span>
                  <div className="font-medium text-puzzle-text">Solo Puzzles</div>
                </div>
                <div className="text-2xl font-bold text-puzzle-green">
                  {stats.completions?.solo?.count || 0}
                </div>
              </div>
            </div>
            <div className="bg-puzzle-background rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">👥</span>
                  <div className="font-medium text-puzzle-text">Room Puzzles</div>
                </div>
                <div className="text-2xl font-bold text-puzzle-yellow">
                  {stats.completions?.room?.count || 0}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Averages */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">Averages</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <svg className="w-5 h-5 text-puzzle-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-puzzle-text-secondary">Avg Time</span>
              </div>
              <div className="text-xl font-bold text-puzzle-text">
                {stats.avgPlayTime ? formatTime(Math.round(stats.avgPlayTime)) : '--:--'}
              </div>
              <div className="text-xs text-puzzle-text-secondary mt-1">per puzzle</div>
            </div>
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <svg className="w-5 h-5 text-puzzle-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
                <span className="text-puzzle-text-secondary">Avg Moves</span>
              </div>
              <div className="text-xl font-bold text-puzzle-text">
                {stats.avgMoves ? Math.round(stats.avgMoves) : '--'}
              </div>
              <div className="text-xs text-puzzle-text-secondary mt-1">per puzzle</div>
            </div>
          </div>
        </section>

        {/* Daily Streak */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">Daily Streak</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="text-3xl mb-1">🔥</div>
              <div className="text-2xl font-bold text-orange-500">
                {streak.current || 0}
              </div>
              <div className="text-sm text-puzzle-text-secondary">Current Streak</div>
            </div>
            <div className="bg-puzzle-background rounded-xl p-4 text-center">
              <div className="text-3xl mb-1">🏆</div>
              <div className="text-2xl font-bold text-yellow-500">
                {streak.longest || 0}
              </div>
              <div className="text-sm text-puzzle-text-secondary">Best Streak</div>
            </div>
          </div>
        </section>

        {/* League */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">League</h2>
          {league.currentTier ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LeagueBadge tier={league.currentTier} size="md" />
                <div>
                  <div className="text-sm text-puzzle-text-secondary">
                    All-time:
                    {' '}
                    <span className="font-semibold text-puzzle-text">
                      {league.currentRating}
                      {' '}
                      pts
                    </span>
                  </div>
                  {league.highestTier && league.highestTier !== league.currentTier && (
                    <div className="text-sm text-puzzle-text-secondary">
                      Highest:
                      {' '}
                      <LeagueBadge tier={league.highestTier} size="sm" showLabel />
                    </div>
                  )}
                </div>
              </div>
              <Link
                to="/league"
                className="text-puzzle-blue hover:underline text-sm font-medium"
              >
                View League
              </Link>
            </div>
          ) : (
            <div className="text-center py-2">
              <p className="text-sm text-puzzle-text-secondary">
                Not in leagues yet
              </p>
            </div>
          )}
        </section>

        {/* Badges */}
        <section className="bg-puzzle-surface rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-puzzle-text mb-4">Badges</h2>
          <BadgeDisplay
            definitions={badges.definitions}
            earned={badges.earned}
          />
        </section>
      </div>
    </div>
  );
}

export default Profile;
